import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { creditsAPI } from '../../services/api';
import type { CreditBalance, CreditTransaction } from '../../types';
import { track } from '../../utils/analytics';
import { AnalyticsEvents } from '../../utils/analyticsEvents';
import { TrendingUp, TrendingDown, RefreshCw, Clock, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import type { ReactNode } from 'react';

export function useCreditsPage() {
  const navigate = useNavigate();
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<keyof CreditTransaction>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const loadCreditData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [balanceResponse, transactionsResponse] = await Promise.all([
        creditsAPI.getBalance(),
        creditsAPI.getTransactions(0, 1000)
      ]);
      setBalance(balanceResponse);
      setTransactions(transactionsResponse.transactions);
    } catch (err) {
      console.error('Error loading credit data:', err);
      setError('Failed to load credit information');
    } finally {
      setLoading(false);
      setBalanceLoading(false);
      setTransactionsLoading(false);
    }
  };

  useEffect(() => {
    loadCreditData();
  }, []);

  const handleRefresh = () => loadCreditData();

  const formatTransactionType = (transaction: CreditTransaction) => {
    switch (transaction.type) {
      case 'purchase': return 'Credit Purchase';
      case 'consumption':
        if (transaction.reference_type === 'sms') return 'SMS Sent';
        if (transaction.reference_type === 'email') return 'Email sent';
        return 'Call Made';
      case 'refund': return 'Refund';
      case 'adjustment': return 'Adjustment';
      default: return transaction.type;
    }
  };

  const formatTransactionAmount = (amount: number) => (amount > 0 ? `+${amount}` : `${amount}`);

  const getTransactionIcon = (transaction: CreditTransaction): ReactNode => {
    if (transaction.type === 'consumption' && (transaction.reference_type === 'sms' || transaction.reference_type === 'email')) {
      return <TrendingDown className="h-5 w-5 text-orange-500" />;
    }
    switch (transaction.type) {
      case 'purchase': return <TrendingUp className="h-5 w-5 text-green-600" />;
      case 'consumption': return <TrendingDown className="h-5 w-5 text-red-600" />;
      case 'refund': return <RefreshCw className="h-5 w-5 text-blue-600" />;
      default: return <Clock className="h-5 w-5 text-gray-300" />;
    }
  };

  const handleSort = (field: keyof CreditTransaction) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: keyof CreditTransaction): ReactNode => {
    if (sortField !== field) return <ChevronsUpDown className="h-4 w-4 text-gray-400" />;
    return sortDirection === 'asc' ? <ChevronUp className="h-4 w-4 text-white" /> : <ChevronDown className="h-4 w-4 text-white" />;
  };

  const sortedTransactions = [...transactions].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    return 0;
  });

  const totalPages = Math.ceil(sortedTransactions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTransactions = sortedTransactions.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => setCurrentPage(page);

  return {
    navigate,
    balance,
    transactions,
    loading,
    balanceLoading,
    transactionsLoading,
    error,
    sortField,
    sortDirection,
    currentPage,
    itemsPerPage,
    totalPages,
    startIndex,
    endIndex,
    paginatedTransactions,
    sortedTransactions,
    loadCreditData,
    handleRefresh,
    formatTransactionType,
    formatTransactionAmount,
    getTransactionIcon,
    handleSort,
    getSortIcon,
    handlePageChange,
  };
}
